import { GetNotes, PostNote, PostNotesPage } from '../redux/dashboardSlice';
import { splitLongPlainTitle } from '../components/Journal/journalUtils';
import { stripInlineCitationRefs } from './intelligenceInsightsMeta';
import { markdownToJournalHtml } from './markdownToJournalHtml';

export const ASK_LENS_OUTPUT_SECTION_NAME = 'Ask Lens Output';

export function isAskLensOutputSection(section) {
    return section?.name === ASK_LENS_OUTPUT_SECTION_NAME;
}

export function findAskLensOutputSection(notesState) {
    return (notesState || []).find(section => isAskLensOutputSection(section)) || null;
}

function escapeHtml(text) {
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/**
 * Journal editor title field is capped at ~45 chars on load — keep h2 short and
 * preserve the full question/headline in the body.
 */
export function buildAskLensJournalEntryHtml(query, outputText) {
    const fullQuery = String(query || '').trim();
    const { title } = splitLongPlainTitle(fullQuery || 'Ask Lens output');
    const heading = escapeHtml(title || 'Ask Lens output');
    const bodyMarkdown = stripInlineCitationRefs(String(outputText || '')).trim();
    const bodyHtml = markdownToJournalHtml(bodyMarkdown);

    const parts = [];
    if (fullQuery) {
        parts.push(`<h3>Question</h3><p>${escapeHtml(fullQuery)}</p>`);
    }
    if (bodyHtml) {
        parts.push(bodyHtml);
    } else {
        parts.push('<p></p>');
    }

    return `<h2>${heading}</h2>${parts.join('')}`;
}

function joinJournalSections(...parts) {
    return parts
        .flatMap(part => {
            if (part == null) return [];
            if (Array.isArray(part)) return part;
            return [part];
        })
        .map(part => String(part).trim())
        .filter(Boolean)
        .join('\n\n');
}

function listSection(label, items) {
    if (!Array.isArray(items) || !items.length) return '';
    return `${label}:\n${items.map(item => `- ${item}`).join('\n')}`;
}

/** Full item body for Insights journal saves (card + detail drawer fields). */
export function buildInsightsItemJournalBody(item) {
    if (!item) return '';
    const detail = item.detail || {};
    const potentialImpact = detail.potentialImpact;
    return joinJournalSections(
        detail.fullAssessment || item.body,
        detail.missingOrUnresolved,
        detail.whyItMatters || item.whyItMatters,
        detail.whyRisk,
        detail.strategicImplication,
        Array.isArray(potentialImpact)
            ? potentialImpact.filter(Boolean).join('. ')
            : potentialImpact,
        detail.assessmentLimitations,
        detail.recommendedResponse,
        detail.recommendedRequest,
        item.consequence,
        item.relevance,
        item.affectedPosture,
    );
}

/** Full Highlights card body for journal saves. */
export function buildHighlightsCardJournalBody(card) {
    if (!card) return '';
    const detail = card.detail || {};
    return joinJournalSections(
        detail.fullFinding || card.conclusion,
        card.whyItMatters,
        detail.assessmentRationale,
        detail.materialPositives,
        detail.materialConcerns,
        detail.assessmentLimitations,
    );
}

/** Management Summary overall block for journal saves. */
export function buildManagementSummaryJournalBody(data, contextText = '') {
    if (String(contextText || '').trim()) {
        return String(contextText).trim();
    }
    const overall = data?.overall || {};
    const detail = data?.overallDetail || {};
    return joinJournalSections(
        overall.headline,
        overall.summary,
        data?.companyOverview,
        listSection('Supporting strengths', detail.supportingStrengths),
        listSection('Constraining factors', detail.constrainingFactors),
        listSection('Cross-cutting implications', detail.crossCuttingImplications),
        listSection('Assessment limitations', detail.assessmentLimitations),
    );
}

/** Investment Opinion posture block for journal saves. */
export function buildInvestmentOpinionJournalBody(data, contextText = '') {
    if (String(contextText || '').trim()) {
        return String(contextText).trim();
    }
    return joinJournalSections(
        data?.posture?.headline,
        data?.posture?.summary,
        data?.investmentView?.summary,
    );
}

/** Intelligence Insights module save — short h2 (module name only), full headline + body below. */
export function buildInsightsJournalEntryHtml({ moduleLabel, headline, bodyText } = {}) {
    const modulePart = String(moduleLabel || '').trim() || 'Intelligence Insights';
    const headlinePart = String(headline || '').trim();
    const { title, overflow } = splitLongPlainTitle(modulePart);
    const heading = escapeHtml(title || modulePart);

    const parts = [];
    if (overflow) {
        parts.push(`<p>${escapeHtml(overflow)}</p>`);
    }
    if (headlinePart) {
        parts.push(`<h3>${escapeHtml(headlinePart)}</h3>`);
    }
    const bodyMarkdown = stripInlineCitationRefs(String(bodyText || '')).trim();
    if (bodyMarkdown) {
        parts.push(markdownToJournalHtml(bodyMarkdown));
    }
    if (!parts.length) {
        parts.push('<p></p>');
    }

    return `<h2>${heading}</h2>${parts.join('')}`;
}

async function postJournalNotes({ dispatch, notesState, notesHtml }) {
    let currentNotes = notesState;
    if (!Array.isArray(currentNotes) || currentNotes.length === 0) {
        const refreshed = await dispatch(GetNotes()).unwrap();
        currentNotes = refreshed?.data || [];
    }

    const sectionId = await ensureAskLensOutputSection(dispatch, currentNotes);
    if (!sectionId) {
        throw new Error('Could not create Ask Lens Output section.');
    }

    await dispatch(
        PostNote({
            notepad: sectionId,
            notes: notesHtml,
        }),
    ).unwrap();

    return sectionId;
}

export async function ensureAskLensOutputSection(dispatch, notesState) {
    const existing = findAskLensOutputSection(notesState);
    if (existing?.id) return existing.id;

    const result = await dispatch(
        PostNotesPage({
            name: ASK_LENS_OUTPUT_SECTION_NAME,
            project: localStorage['project_id'],
        }),
    ).unwrap();

    return result?.data?.id;
}

export async function saveAskLensOutputToJournal({ dispatch, notesState, query, outputText }) {
    const trimmedQuery = String(query || '').trim();
    const trimmedOutput = String(outputText || '').trim();
    if (!trimmedQuery || !trimmedOutput) {
        throw new Error('Missing query or output to save.');
    }

    const notes = buildAskLensJournalEntryHtml(trimmedQuery, trimmedOutput);
    return postJournalNotes({ dispatch, notesState, notesHtml: notes });
}

export async function saveInsightsModuleToJournal({
    dispatch,
    notesState,
    moduleLabel,
    headline,
    bodyText,
}) {
    const trimmedBody = String(bodyText || '').trim();
    if (!trimmedBody && !headline) {
        throw new Error('Nothing to save to Journal.');
    }

    const notes = buildInsightsJournalEntryHtml({
        moduleLabel,
        headline,
        bodyText: trimmedBody,
    });
    return postJournalNotes({ dispatch, notesState, notesHtml: notes });
}
